// Display formatting for the map v2 page (French locale), following the maquette (carto-b.html).

export const EMPTY_VALUE = '—';

const frNumber = (value: number, decimals: number): string =>
    value.toLocaleString('fr-FR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

// "3 258 km", or "12,4 km" below 100 km (one decimal). Null means "nothing to measure" and shows a dash.
export const formatKilometers = (meters: number | null): string => {
    if (meters === null) {
        return EMPTY_VALUE;
    }
    const kilometers = meters / 1000;

    return `${frNumber(kilometers, kilometers < 100 ? 1 : 0)} km`;
};

// "3,8 M€" from one million, "45 k€" from one thousand, plain euros below. Null means "no project".
export const formatBudget = (euros: number | null): string => {
    if (euros === null) {
        return EMPTY_VALUE;
    }
    if (euros >= 1e6) {
        return `${frNumber(Math.round(euros / 1e5) / 10, 1)} M€`;
    }
    if (euros >= 1e3) {
        return `${frNumber(Math.round(euros / 1e3), 0)} k€`;
    }

    return `${frNumber(Math.round(euros), 0)} €`;
};

export const formatMeters = (meters: number): string => `${frNumber(Math.round(meters), 0)} m`;

type PrSection = {
    roadName: string;
    prd: string | null;
    abd: number | null;
    prf: string | null;
    abf: number | null;
};

// "PR 12+450" (the abscissa is padded to three digits).
const formatPr = (pr: string, abscissa: number | null): string => `${pr}+${String(abscissa ?? 0).padStart(3, '0')}`;

// Readable segment identifier, like "D888 - PR 12+450 à 13+250". Falls back to the road name alone when
// the section has no reference points.
export const formatSectionLabel = ({ roadName, prd, abd, prf, abf }: PrSection): string => {
    if (prd === null || prf === null) {
        return roadName;
    }

    return `${roadName} - PR ${formatPr(prd, abd)} à ${formatPr(prf, abf)}`;
};

type AttributeValue = string | number | null;

// A referential attribute as shown in the object sheet and the multi-selection summary: a dash when
// empty, the width in meters (as in the maquette), anything else as is.
export const formatAttribute = (field: string, value: AttributeValue): string => {
    if (value === null || value === '') {
        return EMPTY_VALUE;
    }

    return field === 'width' ? `${value} m` : String(value);
};
